/**
 * useMarketplaceBrowse — TanStack Query v5 infinite-query data layer for the
 * marketplace browse grid (STORY-003-005).
 *
 * Server state (the paginated product list) lives in TanStack Query. The
 * browse page calls `GET /marketplace/search?mode=browse` (STORY-003-009; the
 * additive empty-q list-all path that 003-002's mode=search refuses). The hook
 * mirrors the backend `SearchResponse` / `SearchResultItem` Pydantic shape
 * (`apps/api/garden/features/marketplace/schemas.py`) with hand-written local
 * interfaces — the same no-codegen pattern `useCategoryTree.ts` follows
 * (`packages/shared-types` is still a stub; do NOT import generated types).
 *
 * Query-param names are the REAL backend names (verified against
 * `public_router.py::public_search`): `mode`, `category` (uuid), `market_id`
 * (repeatable), `in_stock_only` (bool), `min_price`, `max_price` (decimal),
 * `sort`, `cursor`, `limit`. The browser query-string keys the page syncs to
 * the URL are the story's shorthand (`market`, `in_stock`, `min`, `max`) — the
 * translation to backend names happens HERE, at the API seam.
 *
 * No PII travels here — products, prices, markets and pavilion numbers are
 * public catalog data. (Seller phone numbers are NOT in the browse payload;
 * the contact deep links live on the seller-detail page, STORY-003-006.)
 */
import {
  useInfiniteQuery,
  type InfiniteData,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";

/**
 * One product hit in the `/marketplace/search` response (mirrors backend
 * `SearchResultItem`). `price_kgs` is nullable server-side (`Numeric(12,2)`);
 * `photo_thumb_url` is the fully-qualified 200x200 CDN URL the router composes
 * (or the placeholder when the product has no clean image). `market_name_ru`
 * is RU-only server-side — the browse card shows it verbatim (the catalog has
 * no localized market name in the search projection; EN/KY fall back to RU per
 * REQ-PRD-§13.2-A, and the field is a proper noun anyway).
 */
export interface ProductSearchItem {
  id: string;
  name: string;
  price_kgs: number | null;
  /**
   * The high end of a price band and its shape (v0052). `price_kgs` alone is
   * the LOW end, so a grid card rendering it by itself quotes 5 500 for a
   * product that sells at 5 500–14 000 — which half the catalogue does.
   */
  price_max_kgs: number | null;
  price_kind: string | null;
  /** Resolved seller contact (v0053) — never a sentinel number. */
  seller_phone_e164: string | null;
  seller_whatsapp_e164: string | null;
  stock_state: string;
  seller_id: string;
  seller_business_name: string;
  market_id: string;
  market_name_ru: string;
  pavilion_number: string;
  photo_thumb_url: string;
}

/** Response envelope of `GET /marketplace/search` (mirrors `SearchResponse`). */
export interface SearchResponse {
  items: ProductSearchItem[];
  /** Null when this is the last page. */
  next_cursor: string | null;
  /** EXPLAIN row-count estimate; null on cursor follow-up pages. */
  total_estimate: number | null;
  /** True when the result set is empty (search mode only; browse is always false). */
  ooc_offer: boolean;
}

/**
 * The filter state the browse page drives the query from. These are the
 * IN-MEMORY filter fields; the hook maps them to the backend query-param names.
 * All optional — an empty filter set lists all products (browse mode).
 */
export interface BrowseFilters {
  /** Single category subtree filter (`category=<uuid>` server-side). */
  categoryId?: string | null;
  /** Repeatable market multi-select (`market_id=<uuid>&market_id=…` server-side). */
  marketIds?: readonly string[];
  /** `in_stock_only=true` → stock_state IN ('in_stock','low'). */
  inStockOnly?: boolean;
  /** Decimal KGS lower bound (`min_price` server-side; NO tiyin conversion). */
  minPrice?: number | null;
  /** Decimal KGS upper bound (`max_price` server-side; NO tiyin conversion). */
  maxPrice?: number | null;
  /** `relevance` (browse → recency), `price_asc`, `price_desc`. */
  sort?: string;
}

/** The public search endpoint (STORY-003-002 / -009). */
export const SEARCH_PATH = "/marketplace/search";

/** Per-page item count (mirrors backend SEARCH_DEFAULT_LIMIT = 25). */
export const BROWSE_PAGE_LIMIT = 25;

/** staleTime per the story Implementation logic data-fetching block (30s). */
export const BROWSE_STALE_TIME_MS = 30_000;

/**
 * Stable query key. Reflects the filter snapshot so a filter change yields a
 * fresh cache entry (and the grid is REPLACED, not appended — AC #2). The
 * `sort` is part of the key too so a re-sort re-issues from page 1.
 */
export function marketplaceBrowseQueryKey(
  filters: BrowseFilters,
): readonly unknown[] {
  return [
    "marketplace",
    "browse",
    {
      categoryId: filters.categoryId ?? null,
      marketIds: [...(filters.marketIds ?? [])].sort(),
      inStockOnly: filters.inStockOnly ?? false,
      minPrice: filters.minPrice ?? null,
      maxPrice: filters.maxPrice ?? null,
      sort: filters.sort ?? "relevance",
    },
  ];
}

/**
 * Build the `GET /marketplace/search?mode=browse&…` path with the REAL backend
 * query-param names. Exported (not just internal) so a test can assert the
 * exact URL the hook requests. `mode=browse` is always set on the browse page;
 * `q` is intentionally omitted (browse permits an absent q and ignores it for
 * ranking — STORY-003-009).
 */
export function buildBrowseSearchPath(
  filters: BrowseFilters,
  cursor: string | null,
): string {
  const params = new URLSearchParams();
  params.set("mode", "browse");
  if (filters.categoryId) {
    params.set("category", filters.categoryId);
  }
  for (const marketId of filters.marketIds ?? []) {
    params.append("market_id", marketId);
  }
  if (filters.inStockOnly) {
    params.set("in_stock_only", "true");
  }
  if (filters.minPrice !== null && filters.minPrice !== undefined) {
    params.set("min_price", String(filters.minPrice));
  }
  if (filters.maxPrice !== null && filters.maxPrice !== undefined) {
    params.set("max_price", String(filters.maxPrice));
  }
  // Browse ignores `relevance` for ranking (always created_at DESC) but the
  // price sorts still apply, so forward the non-default sort verbatim.
  const sort = filters.sort ?? "relevance";
  if (sort === "price_asc" || sort === "price_desc") {
    params.set("sort", sort);
  }
  params.set("limit", String(BROWSE_PAGE_LIMIT));
  if (cursor) {
    params.set("cursor", cursor);
  }
  return `${SEARCH_PATH}?${params.toString()}`;
}

export type UseMarketplaceBrowseResult = UseInfiniteQueryResult<
  InfiniteData<SearchResponse, string | null>,
  Error
>;

/**
 * Fetch + cache the paginated browse results. Pages are appended via the
 * `next_cursor` from the previous page (AC #9); the query stops requesting
 * when `next_cursor` is null (`getNextPageParam` returns `undefined`).
 */
export function useMarketplaceBrowse(
  filters: BrowseFilters,
): UseMarketplaceBrowseResult {
  return useInfiniteQuery<
    SearchResponse,
    Error,
    InfiniteData<SearchResponse, string | null>,
    readonly unknown[],
    string | null
  >({
    queryKey: marketplaceBrowseQueryKey(filters),
    queryFn: ({ pageParam }): Promise<SearchResponse> =>
      apiClient.get<SearchResponse>(buildBrowseSearchPath(filters, pageParam)),
    initialPageParam: null,
    getNextPageParam: (lastPage): string | null | undefined =>
      lastPage.next_cursor ?? undefined,
    staleTime: BROWSE_STALE_TIME_MS,
  });
}

/** Flatten the infinite-query pages into a single item list (render input). */
export function flattenBrowsePages(
  data: InfiniteData<SearchResponse, string | null> | undefined,
): ProductSearchItem[] {
  if (!data) return [];
  return data.pages.flatMap((page) => page.items);
}
